export interface ParsedSseEvent {
  event: string;
  data: unknown;
}

function parseSseDataPayload(dataLines: string[]): unknown {
  const rawData = dataLines.map((line) => line.replace(/\r$/, "")).join("\n");

  if (rawData === "null") {
    return null;
  }

  try {
    return JSON.parse(rawData);
  } catch {
    // Elysia may emit string deltas as raw `data: token` (not JSON-quoted).
    return rawData;
  }
}

/** Parse one complete SSE event block (lines between blank-line delimiters). */
export function parseSseBlock(block: string): ParsedSseEvent | null {
  const normalized = block.replace(/\r\n/g, "\n").replace(/^\n+/, "").replace(/\n+$/, "");
  if (!normalized) return null;

  const lines = normalized.split("\n");
  let event: string | null = null;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event: ")) {
      event = line.slice("event: ".length).trim();
      continue;
    }

    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
      continue;
    }

    if (line.startsWith("data: ")) {
      dataLines.push(line.slice("data: ".length));
      continue;
    }

    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length));
      continue;
    }

    if (dataLines.length > 0) {
      // Non-standard continuation lines emitted by some SSE serializers.
      dataLines[dataLines.length - 1] += `\n${line}`;
    }
  }

  if (!event || dataLines.length === 0) {
    return null;
  }

  return {
    event,
    data: parseSseDataPayload(dataLines),
  };
}

interface SseParseState {
  event: string | null;
  dataLines: string[];
}

function dispatchSseEvent(
  state: SseParseState,
  onEvent: (event: ParsedSseEvent) => void,
): SseParseState {
  if (state.event && state.dataLines.length > 0) {
    onEvent({
      event: state.event,
      data: parseSseDataPayload(state.dataLines),
    });
  }

  return { event: null, dataLines: [] };
}

function consumeSseLine(line: string, state: SseParseState): SseParseState {
  if (line.startsWith("event: ")) {
    return { ...state, event: line.slice("event: ".length).trim() };
  }

  if (line.startsWith("event:")) {
    return { ...state, event: line.slice("event:".length).trim() };
  }

  if (line.startsWith("data: ")) {
    return { ...state, dataLines: [...state.dataLines, line.slice("data: ".length)] };
  }

  if (line.startsWith("data:")) {
    return { ...state, dataLines: [...state.dataLines, line.slice("data:".length)] };
  }

  if (state.dataLines.length > 0) {
    const dataLines = [...state.dataLines];
    dataLines[dataLines.length - 1] += `\n${line}`;
    return { ...state, dataLines };
  }

  return state;
}

function serializeSseState(state: SseParseState, incompleteLine: string): string {
  const lines: string[] = [];

  if (state.event) {
    lines.push(`event: ${state.event}`);
  }

  for (const dataLine of state.dataLines) {
    lines.push(`data: ${dataLine}`);
  }

  if (incompleteLine) {
    lines.push(incompleteLine);
  }

  return lines.join("\n");
}

/**
 * Split an SSE buffer on blank lines, invoke `onEvent` for complete blocks,
 * and return any trailing incomplete block.
 */
export function feedSseBuffer(
  buffer: string,
  onEvent: (event: ParsedSseEvent) => void,
): string {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const hasTrailingNewline = normalized.endsWith("\n");
  const lines = normalized.split("\n");
  const incompleteLine = hasTrailingNewline ? "" : (lines.pop() ?? "");

  let state: SseParseState = { event: null, dataLines: [] };

  for (const line of lines) {
    if (line === "") {
      state = dispatchSseEvent(state, onEvent);
      continue;
    }

    state = consumeSseLine(line, state);
  }

  return serializeSseState(state, incompleteLine);
}
